import {
  BottomSheetContainer,
  createUseBottomSheetModal,
  Flex,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import { useEffect, useMemo, useState } from 'react';
import { SelectOptionView } from './components/SelectOptionView';
import type { GoodsOption } from 'shared/src/api';
import { PricingListView } from './components/ProductListView';
import { SelectOptionsContext } from './context/SelectOptionsContext';
import { useLogViewGoodsOptionModal } from './hooks/useLogViewGoodsOptionModal';
import { isNotNil } from 'shared/src/utils';
import { systemAlert } from 'shared/src/component';

export const useSelectOptionBottomSheet = createUseBottomSheetModal<
  { lastAction: 'checkout' | 'addToCart' | 'close' | 'needSignup' },
  {
    mode: 'purchase' | 'gift';
    isShowOnce: boolean;
    selectedOptions: GoodsOption[];
    updateSelectedOption: (options: GoodsOption[]) => void;
  }
>(({ param, confirm }) => {
  const { height: screenHeight } = useScreenSize();
  const { top, bottom } = useSafeArea();
  const [type, setType] = useState<'select' | 'pricing'>(
    param.selectedOptions.length > 0 ? 'pricing' : 'select',
  );
  const [selectedOptions, setSelectedOptions] = useState<GoodsOption[]>(
    param.selectedOptions,
  );

  const bottomSheetHeight = useMemo(() => {
    return (screenHeight - top - bottom) * 0.8;
  }, [bottom, screenHeight, top]);
  useLogViewGoodsOptionModal(param);

  useEffect(() => {
    param.updateSelectedOption(selectedOptions);
  }, [selectedOptions, param]);

  return (
    <BottomSheetContainer>
      <SelectOptionsContext.Provider value={{ mode: param.mode }}>
        <Flex height={bottomSheetHeight}>
          {type === 'select' && (
            <SelectOptionView
              onSelectOptionComplete={option => {
                setSelectedOptions(prev => {
                  const sameOption = prev.find(
                    prevOption => prevOption.sno === option.sno,
                  );
                  if (isNotNil(sameOption)) {
                    const currentCount = sameOption.purchase_count ?? 1;
                    const maxLimit = isNotNil(option.max_order_limit_count)
                      ? Math.min(option.stock, option.max_order_limit_count)
                      : option.stock;

                    if (currentCount >= maxLimit) {
                      systemAlert(
                        `${maxLimit}개까지 구매 가능한 옵션이에요`,
                        `${option.option_names.join('/')} 옵션은 인당 최대 ${maxLimit}개까지 구매할 수 있어요.`,
                        [{ text: '확인' }],
                      );
                      return prev;
                    }

                    return prev.map(prevOption => {
                      if (prevOption.sno === option.sno) {
                        return {
                          ...prevOption,
                          purchase_count: currentCount + 1,
                        };
                      }
                      return prevOption;
                    });
                  }

                  return [
                    {
                      ...option,
                      purchase_count: Math.max(option.purchase_count ?? 0, 1),
                    },
                    ...prev,
                  ];
                });
                setType('pricing');
              }}
              onPressClose={() => {
                if (selectedOptions.length === 0) {
                  confirm({ lastAction: 'close' });
                } else {
                  setType('pricing');
                }
              }}
            />
          )}
          {type === 'pricing' && (
            <PricingListView
              selected={selectedOptions}
              onCompleteAddToCart={() => {
                confirm({ lastAction: 'addToCart' });
              }}
              onCompletePurchase={() => {
                confirm({ lastAction: 'checkout' });
              }}
              onUpdateOption={option => {
                setSelectedOptions(prev =>
                  prev.map(prevOption => {
                    if (prevOption.sno === option.sno) {
                      return {
                        ...prevOption,
                        ...option,
                      };
                    }
                    return prevOption;
                  }),
                );
              }}
              onSelectMore={() => setType('select')}
              onPressDelete={option => {
                setSelectedOptions(prev =>
                  prev.filter(prevOption => prevOption.sno !== option.sno),
                );
              }}
              onAnonymouseUserPressedPurchase={() => {
                confirm({ lastAction: 'needSignup' });
              }}
            />
          )}
        </Flex>
      </SelectOptionsContext.Provider>
    </BottomSheetContainer>
  );
});
